import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Fleet } from '../../../../types/fleet';
import Accordion from 'devextreme-react/accordion';
import CheckBox from 'devextreme-react/check-box';

export default function FleetAccordion({
  fleetList,
  onFleetSelectionChange,
  selectedFleetIds,
}: {
  fleetList: Fleet[];
  onFleetSelectionChange?: (selectedFleetIds: number[]) => void;
  selectedFleetIds: number[];
}) {
  const { t } = useTranslation();
  const [selectedItems, setSelectedItems] = useState<any>([]);

  const accordionItems = useMemo(() => {
    return [
      {
        title: t('labels.fleets'),
        visible: true,
      },
    ];
  }, [t]);

  useEffect(() => {
    setSelectedItems(accordionItems);
  }, [accordionItems]);

  const handleFleetCheckboxChange = (fleetId: number, checked: boolean) => {
    const newSelectedFleetIds = checked
      ? [...selectedFleetIds, fleetId]
      : selectedFleetIds.filter((id) => id !== fleetId);
    if (onFleetSelectionChange) {
      onFleetSelectionChange(newSelectedFleetIds);
    }
  };

  const handleSelectAll = () => {
    const allIds = fleetList.map((fleet) => fleet.fleetId);
    if (onFleetSelectionChange) {
      onFleetSelectionChange(allIds);
    }
  };

  const handleUnselectAll = () => {
    if (onFleetSelectionChange) {
      onFleetSelectionChange([]);
    }
  };

  const accordionItemRender = (e: any) => {
    if (e.title === t('labels.fleets')) {
      return (
        <div className="m-c-checkbox-dark">
          {fleetList.map((fleet) => (
            <div
              className="m-l-grid-top-header-list-column p-2"
              key={fleet.fleetId}
            >
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <CheckBox
                  key={`fleet-checkbox-${
                    fleet.fleetId
                  }-${selectedFleetIds.includes(fleet.fleetId)}`}
                  elementAttr={{ 'data-testid': `fleet-${fleet.fleetId}` }}
                  value={selectedFleetIds.includes(fleet.fleetId)}
                  onValueChanged={(e) =>
                    handleFleetCheckboxChange(fleet.fleetId, e.value)
                  }
                />
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill={fleet.color}
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ transform: 'rotate(90deg)' }}
                >
                  <path
                    opacity="0.15"
                    d="M12 3L19 20L12 17L5 20L12 3Z"
                    fill={fleet.color}
                  />
                  <path
                    d="M19 20L12 3L5 20L12 17L19 20Z"
                    stroke={fleet.color}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>{fleet.fleetName}</span>
              </div>
            </div>
          ))}
          {fleetList.length > 0 && (
            <div className="checkbox-modes">
              <a className="m-l-page-id-text" onClick={handleSelectAll}>
                {t('buttons.selectAll')}
              </a>
              <a className="m-l-page-id-text" onClick={handleUnselectAll}>
                {t('buttons.unselectAll')}
              </a>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  const selectionChanged = (e: any) => {
    let newItems: any = [...selectedItems];
    e.removedItems.forEach((item: any) => {
      const index = newItems.findIndex(
        (selectedItem: any) => selectedItem.title === item.title
      );
      if (index >= 0) {
        newItems.splice(index, 1);
      }
    });
    if (e.addedItems.length) {
      newItems = [...newItems, ...e.addedItems];
    }
    setSelectedItems(newItems);
  };

  return (
    <div className="m-l-accordion-panel-body-block">
      <div className="m-l-accordion__main-section u-p0">
        <div className="m-c-accordion">
          <Accordion
            collapsible={true}
            multiple={false}
            items={accordionItems}
            itemRender={accordionItemRender}
            selectedItems={selectedItems}
            onSelectionChanged={selectionChanged}
          />
        </div>
      </div>
    </div>
  );
}
